import { Mesh, SolidParticle, SolidParticleSystem, Vector3 } from '@babylonjs/core';
import { Level } from './Level';

export class MeshExplode {
  /**
   * Explode a mesh
   *
   * @param mesh The mesh to explode
   * @param level The level to explode the mesh in
   * @param radius The radius of the explosion
   * @param speed The speed of the explosion
   * @param gravity The gravity of the explosion particles
   * @param facetNb The number of facets to use for the explosion
   * @param delta The delta to use for the explosion
   */
  static explode(
    mesh: Mesh,
    level: Level,
    radius: number = 10,
    speed: number = 5,
    gravity: number = -0.5,
    facetNb: number = 1,
    delta: number = 1
  ): void {
    const sps = new SolidParticleSystem('explodeSps', level.game.scene, { isPickable: false });

    // If mesh is an instance, use its source mesh for geometry digest
    let sourceMesh = mesh;
    if ((mesh as any).sourceMesh) {
      sourceMesh = (mesh as any).sourceMesh;
    }

    // Digest the mesh into particles
    sps.digest(sourceMesh, { facetNb: facetNb, delta: delta });

    if (sps.nbParticles === 0) {
      sps.buildMesh();
      sps.dispose();
      return;
    }

    const s = sps.buildMesh();
    s.material = mesh.material;

    // Position the SPS mesh at the exploded mesh's absolute position
    s.position.copyFrom(mesh.getAbsolutePosition());
    if (mesh.rotationQuaternion) {
      s.rotationQuaternion = mesh.absoluteRotationQuaternion;
    } else {
      s.rotation = mesh.absoluteRotationQuaternion.toEulerAngles();
    }
    s.scaling.copyFrom(mesh.absoluteScaling);

    sps.setParticles();
    sps.refreshVisibleSize();

    // Initialize explosion state
    sps.vars.target = Vector3.Zero(); // Target relative to Center (SPS is now at Center)
    sps.vars.tmp = Vector3.Zero();
    sps.vars.justClicked = true; // Trigger initial velocity immediately
    sps.vars.radius = radius;
    sps.vars.minY = -100;

    let boom = true; // Start explosion immediately

    // Animation loop
    let frameCount = 0;
    const observer = level.game.scene.onBeforeRenderObservable.add(() => {
      const deltaTime = level.game.scene.getEngine().getDeltaTime() / 1000;

      // Pass delta time to sps vars for access in updateParticle
      sps.vars.deltaTime = deltaTime;

      sps.setParticles();
      frameCount++;

      if (frameCount > 300) {
        sps.dispose();
        s.dispose();
        level.game.scene.onBeforeRenderObservable.remove(observer);
      }
    });

    sps.updateParticle = (p: SolidParticle) => {
      const dt = sps.vars.deltaTime || 0.016; // strict fallback
      const rand = Math.random() / 100;

      if (sps.vars.justClicked) {
        // velocity init
        p.velocity.x = (Math.random() - 0.5) * speed;
        p.velocity.y = (Math.random() * 0.5 + 0.5) * speed;
        p.velocity.z = (Math.random() - 0.5) * speed;

        if (p.position.lengthSquared() > 0.001) {
          const dir = p.position.clone().normalize();
          const radialSpeed = speed * (0.5 + Math.random());
          p.velocity.addInPlace(dir.scale(radialSpeed));
        }

        if (p.idx == sps.nbParticles - 1) {
          sps.vars.justClicked = false;
        }
      }

      if (boom && !sps.vars.justClicked) {
        if (p.position.y < sps.vars.minY) {
          p.position.y = sps.vars.minY;
          p.velocity.setAll(0);
        } else {
          // Apply gravity (units/sec^2) * dt
          p.velocity.y += gravity * 60 * dt; // Scaling gravity to match previous expected behavior roughly?

          // Move: pos += vel * dt
          p.position.x += p.velocity.x * dt;
          p.position.y += p.velocity.y * dt;
          p.position.z += p.velocity.z * dt;

          // Rotate
          p.rotation.x += p.velocity.z * rand; // simple random rotation
          p.rotation.y += p.velocity.x * rand;
          p.rotation.z += p.velocity.y * rand;
        }
      }

      return p;
    };
  }
}
